import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useMembership } from '@/hooks/useMembership';
import { usePonds } from '@/hooks/usePonds';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Badge } from '@/components/ui/Badge';
import { SelectInput } from '@/components/entry';
import {
  Calculator,
  Scale,
  Fish,
  Calendar,
  TrendingUp,
  CloudSun,
  AlertTriangle,
  CheckCircle2,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface FeedCalculation {
  doc: number;
  stockingCount: number;
  latestAbw: number | null;
  biomass: number | null;
  survivalRate: number | null;
  feedPercentage: number;
  recommendedAmount: number;
  adjustedAmount: number;
  feedType: string;
  trayAmount: number;
  checkTime: string;
  method: 'doc' | 'abw';
  weatherAdjustment?: {
    reason: string;
    percent: number;
  };
}

interface FeedCalculatorToolProps {
  className?: string;
}

export function FeedCalculatorTool({ className }: FeedCalculatorToolProps) {
  const { membership } = useMembership();
  const [selectedPondId, setSelectedPondId] = useState<string>('');

  // Fetch ponds
  const { data: ponds, isLoading: pondsLoading } = usePonds(membership?.farmId);

  // Fetch feed calculation
  const { data: calculation, isLoading: calcLoading } = useQuery({
    queryKey: ['feed-calculator', selectedPondId],
    queryFn: async () => {
      const response = await api.get<{ data: FeedCalculation }>(
        `/api/v1/farms/${membership?.farmId}/feed-calculator`,
        { params: { pond_id: selectedPondId } }
      );
      return response.data;
    },
    enabled: !!selectedPondId && !!membership?.farmId,
  });


  const pondOptions = useMemo(
    () => ponds?.map((p) => ({ value: p.id, label: p.name })) || [],
    [ponds]
  );

  if (pondsLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 border border-emerald-200">
          <Calculator className="w-6 h-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feed Calculator</h1>
          <p className="text-gray-500">Calculate optimal feed amounts for your ponds</p>
        </div>
      </div>

      {/* Pond Selector */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Pond
        </label>
        <SelectInput
          value={selectedPondId}
          options={pondOptions}
          onChange={setSelectedPondId}
          placeholder="Choose a pond..."
          label=""
        />

        {ponds?.length === 0 && (
          <p className="mt-3 text-sm text-amber-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            No active ponds found
          </p>
        )}
      </div>

      {/* Loading state */}
      {calcLoading && selectedPondId && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size={32} />
        </div>
      )}

      {/* Results */}
      {calculation && !calcLoading && (
        <div className="space-y-5">
          {/* Cycle Information Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
              <h3 className="font-semibold text-gray-900">Cycle Information</h3>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Calendar className="w-4 h-4" />
                    <span className="text-xs font-medium">DOC</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 tabular-nums">
                    {calculation.doc}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Fish className="w-4 h-4" />
                    <span className="text-xs font-medium">Stocking</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 tabular-nums">
                    {calculation.stockingCount.toLocaleString()}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Scale className="w-4 h-4" />
                    <span className="text-xs font-medium">Latest ABW</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 tabular-nums">
                    {calculation.latestAbw?.toFixed(2) || '—'}
                    {calculation.latestAbw && (
                      <span className="text-sm font-normal text-gray-400 ml-1">g</span>
                    )}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-xs font-medium">Est. Biomass</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 tabular-nums">
                    {calculation.biomass?.toFixed(0) || '—'}
                    {calculation.biomass && (
                      <span className="text-sm font-normal text-gray-400 ml-1">kg</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Recommendation Card */}
          <div className="relative overflow-hidden rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
            {/* Decorative pattern */}
            <div className="absolute inset-0 opacity-[0.03]">
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="feed-calc-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="20" cy="20" r="2" fill="currentColor" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#feed-calc-pattern)" />
              </svg>
            </div>

            <div className="relative p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-emerald-900">Recommended Feed</h3>
                </div>
                <Badge
                  variant={calculation.method === 'abw' ? 'ok' : 'neutral'}
                  size="sm"
                >
                  {calculation.method === 'abw' ? 'ABW-based' : 'DOC-based'}
                </Badge>
              </div>

              {/* Main amount */}
              <div className="text-center py-6">
                <p className="text-6xl font-bold text-emerald-700 tabular-nums">
                  {calculation.adjustedAmount.toFixed(1)}
                  <span className="text-2xl font-normal text-emerald-500 ml-2">kg</span>
                </p>
                {calculation.weatherAdjustment && (
                  <p className="text-sm text-emerald-600 mt-2">
                    (Base: {calculation.recommendedAmount.toFixed(1)} kg)
                  </p>
                )}
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
                  <p className="text-xs text-emerald-600 mb-0.5">Feed Type</p>
                  <p className="font-semibold text-emerald-900">{calculation.feedType}</p>
                </div>
                <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
                  <p className="text-xs text-emerald-600 mb-0.5">Tray Amount</p>
                  <p className="font-semibold text-emerald-900">{calculation.trayAmount} g</p>
                </div>
                <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
                  <p className="text-xs text-emerald-600 mb-0.5">Check Time</p>
                  <p className="font-semibold text-emerald-900">{calculation.checkTime}</p>
                </div>
                <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
                  <p className="text-xs text-emerald-600 mb-0.5">Feed %</p>
                  <p className="font-semibold text-emerald-900">{calculation.feedPercentage}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Weather Adjustment Card */}
          {calculation.weatherAdjustment && (
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-5">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <CloudSun className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-amber-900">Weather Adjustment Applied</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    {calculation.weatherAdjustment.reason}
                  </p>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="text-sm">
                      <span className="text-amber-600">Adjustment:</span>{' '}
                      <span className="font-semibold text-amber-900">
                        {calculation.weatherAdjustment.percent > 0 ? '+' : ''}
                        {calculation.weatherAdjustment.percent}%
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-amber-600">Base amount:</span>{' '}
                      <span className="font-semibold text-amber-900">
                        {calculation.recommendedAmount.toFixed(1)} kg
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-amber-600">Adjusted:</span>{' '}
                      <span className="font-semibold text-amber-900">
                        {calculation.adjustedAmount.toFixed(1)} kg
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Method explanation */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-gray-400 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-700 mb-2">How is this calculated?</p>
                {calculation.method === 'abw' ? (
                  <p>
                    Using ABW-based calculation: Biomass × Feed Percentage. This method is more
                    accurate because it uses actual sampling data from your pond.
                  </p>
                ) : (
                  <p>
                    Using DOC-based calculation with standard growth curves. For more accurate
                    results, enter sampling data to enable ABW-based calculations.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!selectedPondId && pondOptions.length > 0 && (
        <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-300">
          <Calculator className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">Select a pond to calculate feed</p>
          <p className="text-sm text-gray-400">
            The calculator will show recommendations based on cycle data
          </p>
        </div>
      )}
    </div>
  );
}
